'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksNeKadarSureGiderPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-ne-kadar-sure-gider';
  const pageTitle = 'Botoks Ne Kadar Süre Gider? - Etki Süresi ve Kalıcılık Rehberi';
  const pageDescription = 'Botoksun etkisi ne kadar sürer? Ortalama kalıcılık süresi, etkisini azaltan faktörler ve sonuçları uzun ömürlü kılmanın yolları.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Ne Kadar Süre Gider?', href: '/tr/blog/botoks-ne-kadar-sure-gider' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks etki süresi ve kalıcılık değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Etki Süresi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              ⏱️ Botoks Ne Kadar Süre Gider?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>22 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (botulinum toksini), mimik kaslarının hareketini geçici olarak durdurarak kırışıklıkları azaltan güvenli bir estetik işlemdir. Peki, botoksun etkisi ne kadar sürer? Bu süre kişiden kişiye değişmekle birlikte, birçok faktör etkinin kalıcılığını belirler.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Ortalama Etki Süresi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Genel olarak botoksun etkisi 3 ila 6 ay arasında sürer. İlk defa botoks yaptıran kişilerde kaslar daha aktif olduğu için etki süresi genellikle 3-4 ay civarındadır. Düzenli uygulamalarda kas hafızası geliştiğinden süre uzar ve 6 aya kadar çıkabilir.
              </p>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">➡️ Etki süresi üzerinde belirleyici olan faktörler:</h3>
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Kullanılan botoks markası ve kalitesi</li>
                  <li>Uygulama yapılan bölge (alın, kaş arası, göz çevresi vb.)</li>
                  <li>Kas gücü ve mimik yoğunluğu</li>
                  <li>Kişisel metabolizma hızı</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Etki Süresini Kısaltan Faktörler
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bazı alışkanlıklar ve yaşam tarzı botoksun etkisini azaltabilir:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Yoğun mimik kullanımı (sık kaş çatma, gülme)</li>
                  <li>Spor ve sauna gibi kas aktivitesini artıran aktiviteler</li>
                  <li>Sigara ve alkol tüketimi</li>
                  <li>Uygulama sonrası bölgeye masaj yapılması</li>
                  <li>Düzensiz uyku ve stres</li>
                </ul>
                <p className="text-red-800 mt-3 font-medium">
                  Bu etkenler botoksun sinir-kas bağlantısında daha hızlı çözülmesine neden olur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Etki Süresini Uzatmanın Yolları
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Daha uzun süre kalıcı bir botoks etkisi için dikkat edilmesi gerekenler:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>İşlemden sonraki ilk 24 saatte yüzünüze masaj yapmayın</li>
                  <li>Aşırı sıcak ortamlardan uzak durun (hamam, sauna, solaryum)</li>
                  <li>Yeterli su tüketin ve düzenli uyuyun</li>
                  <li>Cilt bakım rutininizi düzenli sürdürün</li>
                  <li>İşlemi her 5-6 ayda bir düzenli tekrar ettirin</li>
                </ul>
                <p className="text-green-800 mt-3">
                  Bu şekilde kas gevşemesi sabit hale gelir ve kırışıklık oluşumu yavaşlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kalıcılık Süresi Bölgelerine Göre
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Bölge
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Ortalama Etki Süresi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Alın bölgesi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Kaş arası (glabella)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Göz çevresi (kaz ayağı)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–4 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Boyun ve çene hattı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–6 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Migren veya diş sıkma botoksu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–7 ay</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Etkinin Azaldığını Nasıl Anlarsınız?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun etkisi azalmaya başladığında:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-yellow-800 space-y-2">
                  <li>Mimik hareketleri yavaş yavaş geri döner</li>
                  <li>Kırışıklıklar yeniden belirginleşir</li>
                  <li>Kaslarda hafif gerginlik azalır</li>
                </ul>
                <p className="text-yellow-800 mt-3">
                  Bu durumda doktorunuz kontrol seansı planlayabilir.
                </p>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoks etki süresi değerlendirmesi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Botoksun etkisi neden herkeste farklı sürüyor?</h3>
                  <p className="text-gray-700">Kas yapısı, yaş, metabolizma hızı ve yaşam alışkanlıkları bu süreyi değiştirir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etki geçince yüz eski haline döner mi?</h3>
                  <p className="text-gray-700">Evet, etki geçtikten sonra yüz eski haline döner; ciltte sarkma olmaz.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Botoksu tekrar yaptırmak zararlı mı?</h3>
                  <p className="text-gray-700">Hayır, düzenli aralıklarla yapıldığında güvenlidir; kas hafızası gelişir ve etki süresi uzar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Botoksun etkisini artıran vitamin veya krem var mı?</h3>
                  <p className="text-gray-700">Doğrudan artıran bir krem yoktur, ancak nemlendirici ve güneş koruyucu düzenli kullanmak cilt kalitesini destekler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Etki 2 aydan kısa sürdüyse bu normal mi?</h3>
                  <p className="text-gray-700">Hayır, muhtemelen doz yetersiz kalmış veya ürün seyreltilmiş olabilir. Hekim kontrolü gerekir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Dermatology (AAD) – Botulinum Toxin Clinical Guidelines</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Botoks Uygulama ve Etki Süresi Rehberi</li>
                <li>Mayo Clinic – "How Long Does Botox Last?"</li>
                <li>Harvard Health Publishing – "Maintaining Botox Results Safely"</li>
                <li>FDA – Botulinum Toxin Type A Onay ve Kullanım Bilgileri</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#botoksetkisi</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#kalıcılıksüresi</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#botoksbakımı</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması ve Etki Süresi Hakkında Bilgi Alın</h3>
              <p className="mb-6">Uzman doktorumuzdan botoks etki süresi ve kalıcılık konusunda detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks etki süresi hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-fiyatlari-neden-farkli"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları Neden Farklı?</h4>
              <p className="text-gray-600 text-sm">Botoks fiyatlarındaki farklılıkların nedenleri ve kalite faktörleri.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir? Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoks uygulaması hakkında temel bilgiler ve süreç.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

